
import React, { useState } from 'react';
import { AlertTriangle, User, CheckCircle, XCircle } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PatientInfoCardProps {
  patientId: string;
  initialData: {
    name: string;
    email: string;
    phone: string;
    condition: string;
    notes: string;
    status: 'pending' | 'in progress' | 'completed' | 'archived';
  };
  onSave: (data: any) => Promise<void>;
  refetchData: () => void;
}

const PatientInfoCard: React.FC<PatientInfoCardProps> = ({ 
  patientId, 
  initialData, 
  onSave,
  refetchData 
}) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialData.name);
  const [email, setEmail] = useState(initialData.email);
  const [phone, setPhone] = useState(initialData.phone);
  const [condition, setCondition] = useState(initialData.condition);
  const [notes, setNotes] = useState(initialData.notes);
  const [status, setStatus] = useState<'pending' | 'in progress' | 'completed' | 'archived'>(initialData.status);

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: AlertTriangle },
    { value: 'in progress', label: 'In Progress', icon: User },
    { value: 'completed', label: 'Completed', icon: CheckCircle },
    { value: 'archived', label: 'Archived', icon: XCircle },
  ];

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    // Reset form fields to initial values
    setName(initialData.name);
    setEmail(initialData.email);
    setPhone(initialData.phone);
    setCondition(initialData.condition);
    setNotes(initialData.notes);
    setStatus(initialData.status);
  };

  const handleSaveClick = async () => {
    if (!patientId) {
      toast({
        title: "Missing patient ID",
        description: "Could not update patient data",
        variant: "destructive",
      });
      return;
    }

    const updatedPatientData = {
      name,
      email,
      phone,
      condition,
      notes,
      status,
    };

    try {
      await onSave(updatedPatientData);
      toast({
        title: "Patient updated",
        description: "Patient data has been updated successfully.",
      });
      setIsEditing(false);
      refetchData();
    } catch (error) {
      toast({
        title: "Error updating patient",
        description: "Failed to update patient data.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Patient Information</CardTitle>
        <CardDescription>View and edit patient details</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isEditing}
            />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isEditing}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={!isEditing}
            />
          </div>
          <div>
            <Label htmlFor="condition">Condition</Label>
            <Input
              id="condition"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              disabled={!isEditing}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!isEditing}
            className="min-h-[80px]"
          />
        </div>
        <div>
          <Label htmlFor="status">Status</Label>
          <Select 
            value={status} 
            onValueChange={(value: 'pending' | 'in progress' | 'completed' | 'archived') => setStatus(value)} 
            disabled={!isEditing}
          >
            <SelectTrigger id="status">
              <SelectValue placeholder="Select a status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="flex items-center gap-2">
                  <option.icon className="w-4 h-4" />
                  <span>{option.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        {!isEditing ? (
          <>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Archive Patient</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently archive this patient and remove their data from your
                    account.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction>Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button onClick={handleEditClick}>Edit Patient</Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={handleCancelClick}>Cancel</Button>
            <Button onClick={handleSaveClick}>Save Changes</Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default PatientInfoCard;
